import { PhylloUserAnalyticsData } from '@round/api';
import { Card, Empty, Progress, Spinner, lucideIcons } from '@round/creatorbase-ui';
import cn from 'classnames';
import styles from './Audience.module.css';
import countries from 'utility/countries';
import { toDecimalPoint } from '@round/utils';
import { COUNTRY_COLOR } from './helpers';
import { CSSProperties } from 'react';

const { SearchXIcon, CircleAlertIcon } = lucideIcons;

type Props = {
    data: PhylloUserAnalyticsData['profile']['audience']['countries'];
    isLoading?: boolean;
    errorMessage: string | null;
};

const TopCountries = ({ data, isLoading, errorMessage }: Props) => {
    const isError = !!errorMessage;
    return (
        <Card size="sm" variant="borderless" className={styles.chartContainer}>
            <Card.Header className={styles.chartHeader}>
                <Card.Title>Top Countries</Card.Title>
            </Card.Header>

            <Card.Content className={cn(styles.chartContent, styles.countries)}>
                {isLoading && (
                    <div className={styles.spinnerContainer}>
                        <Spinner className={styles.spinner} />
                    </div>
                )}

                {!isLoading && data.length === 0 && (
                    <Empty>
                        <Empty.Media variant="icon">{isError ? <CircleAlertIcon /> : <SearchXIcon />}</Empty.Media>
                        <Empty.Title>{isError ? 'Something went wrong' : 'No data available'}</Empty.Title>
                        <Empty.Description>
                            {isError
                                ? errorMessage ?? 'Failed to load countries. Please try again.'
                                : 'Please try again later.'}
                        </Empty.Description>
                    </Empty>
                )}

                {!isLoading &&
                    data
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 5)
                        .map((country) => (
                            <div key={country.code} className={styles.countryItem}>
                                <div className={styles.countryItemLabel}>
                                    <p>
                                        <span className={cn(`fi fi-${country.code.toLowerCase()}`, styles.flagIcon)} />
                                        {countries[country.code]}
                                    </p>
                                    <p className={styles.countryItemValue}>{toDecimalPoint(country.value, 2)}%</p>
                                </div>

                                <Progress
                                    value={country.value}
                                    className={styles.countryItemProgress}
                                    style={{ '--progress-indicator-color': COUNTRY_COLOR } as CSSProperties}
                                />
                            </div>
                        ))}
            </Card.Content>
        </Card>
    );
};

export default TopCountries;
